import { CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined, DownloadOutlined, FileExcelOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Table, Tag, Tooltip, Typography } from 'antd';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import api from '~/services/api';
import { Container, HeaderContainer } from './styles';

const { Title, Text } = Typography;

export function InventoryStockReport() {
  const [generating, setGenerating] = useState(false);
  const [reportHistory, setReportHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadReportHistory();

    // Listen for report notifications
    const handleNotification = (e) => {
      const notification = e.detail;
      // console.log('[InventoryStockReport] Received socket notification:', notification);

      if (notification.type === 'report_ready') {
        // console.log('[InventoryStockReport] Report ready, reloading history');
        // Don't show toast here - the Notifications component will show a visual popup
        loadReportHistory();
      } else if (notification.type === 'report_failed') {
        // console.log('[InventoryStockReport] Report failed');
        // Don't show toast here - the Notifications component will show a visual popup
        loadReportHistory();
      }
    };

    // console.log('[InventoryStockReport] Attaching socket-notification event listener');
    window.addEventListener('socket-notification', handleNotification);

    return () => {
      // console.log('[InventoryStockReport] Removing socket-notification event listener');
      window.removeEventListener('socket-notification', handleNotification);
    };
  }, []);

  const loadReportHistory = async () => {
    try {
      setLoading(true);
      const response = await api.get('/reports/inventory-stock/history');

      if (response.data.success && response.data.reports) {
        setReportHistory(response.data.reports);
      } else {
        setReportHistory([]);
      }
    } catch (error) {
      console.error('Error loading report history:', error);
      setReportHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    try {
      setGenerating(true);
      const response = await api.put('/reports/inventory-stock');

      if (response.data.success) {
        toast.success(response.data.message || 'Report generation has been queued. You will be notified when it is ready.');
        // Refresh the report history to show the new pending report
        setTimeout(() => loadReportHistory(), 1000);
      } else {
        toast.error(response.data.message || 'Failed to queue report generation');
      }
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error(error.response?.data?.message || 'Failed to generate report. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async (downloadUrl) => {
    try {
      // Download the file directly
      const response = await api.get(downloadUrl, {
        responseType: 'blob',
      });

      // Create a download link and trigger it
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `Inventory_Stock_Report_${moment().format('YYYY-MM-DD')}.xlsx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Report downloaded successfully');
    } catch (error) {
      console.error('Error downloading report:', error);
      toast.error('Failed to download report. The file may have expired.');
    }
  };

  const getStatusTag = (status) => {
    switch (status) {
      case 'pending':
        return (
          <Tag icon={<ClockCircleOutlined />} color="processing">
            Generating
          </Tag>
        );
      case 'completed':
        return (
          <Tag icon={<CheckCircleOutlined />} color="success">
            Ready
          </Tag>
        );
      case 'failed':
        return (
          <Tag icon={<CloseCircleOutlined />} color="error">
            Failed
          </Tag>
        );
      default:
        return <Tag>{status}</Tag>;
    }
  };

  const columns = [
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => getStatusTag(status),
    },
    {
      title: 'Requested At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (date) => moment(date).format('MMM DD, YYYY h:mm A'),
    },
    {
      title: 'Completed At',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 180,
      render: (date, record) => (record.status === 'completed' ? moment(date).format('MMM DD, YYYY h:mm A') : '-'),
    },
    {
      title: 'Expires At',
      dataIndex: 'expirationDate',
      key: 'expirationDate',
      width: 180,
      render: (date, record) => {
        if (!date || record.status !== 'completed') return '-';
        const isExpired = moment(date).isBefore(moment());
        return (
          <Tooltip title={isExpired ? 'This report has expired' : `Valid until ${moment(date).format('MMM DD, YYYY h:mm A')}`}>
            <span style={{ color: isExpired ? '#ff4d4f' : undefined }}>
              {moment(date).format('MMM DD, YYYY h:mm A')}
              {isExpired && ' (Expired)'}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Action',
      key: 'action',
      width: 120,
      render: (_, record) => {
        if (record.status === 'completed' && record.downloadUrl) {
          const isExpired = record.expirationDate && moment(record.expirationDate).isBefore(moment());
          return (
            <Button type="primary" icon={<DownloadOutlined />} size="small" onClick={() => handleDownload(record.downloadUrl)} disabled={isExpired}>
              Download
            </Button>
          );
        }
        return '-';
      },
    },
  ];

  return (
    <Container>
      <Card>
        <HeaderContainer>
          <div>
            <Title level={3}>Inventory Stock Report</Title>
            <Text type="secondary">
              Generate a comprehensive report of all inventory items with status "InStock" or "ToBeInspected". The report will be available for download for 7
              days after generation.
            </Text>
          </div>
          <Button type="primary" icon={<FileExcelOutlined />} size="large" onClick={handleGenerateReport} loading={generating} disabled={generating}>
            {generating ? 'Generating...' : 'Generate Stock Report'}
          </Button>
        </HeaderContainer>

        <Alert
          message="How it works"
          description={
            <div>
              <p style={{ marginBottom: 8 }}>1. Click "Generate Stock Report" to request a new report. The report generation runs in the background.</p>
              <p style={{ marginBottom: 8 }}>2. You will receive a notification when the report is ready (usually within a few minutes).</p>
              <p style={{ marginBottom: 8 }}>3. Download the Excel file from the notification or from the table below.</p>
              <p style={{ marginBottom: 0 }}>4. Reports are automatically deleted 7 days after generation.</p>
            </div>
          }
          type="info"
          showIcon
          style={{ marginTop: 24, marginBottom: 24 }}
        />

        <Title level={4} style={{ marginBottom: 16 }}>
          Report History
        </Title>
        <Table columns={columns} dataSource={reportHistory} loading={loading} rowKey="id" pagination={{ pageSize: 10 }} size="small" bordered />
      </Card>
    </Container>
  );
}
